import { api } from '../core';
import { CHRONOLOGY_TAG } from '../constants';
import {
  RootState,
  setTimelineChronologyData,
  setShowDateBar,
  TimelineChronologyType,
  TimelineChronologyTypeKeys
} from '@/store';

interface ChronologyResponse {
  startDate: string;
  endDate: string;
  fullBundleId?: string | null;
  lightBundleId?: string | null;
  fullBundlePages?: number;
  lightBundlePages?: number;
  entryGroups: ChronologyEntryGroup[];
}
interface ChronologyEntryGroup {
  date?: string | null;
  entries: ChronologyEntry[];
}

interface ChronologyEntry {
  comment: string;
  date: string;
  pages: ChronologyEntryPage[];
  providerId: string;
  providerName: string;
  verbatimExtract: string;
  chronologyEntryId: string;
  time: string | null;
  containsAdditionalRecords?: boolean;
}

interface ChronologyEntryPage {
  pageId: string;
  fullBundlePageNumber?: number | null;
  lightBundlePageNumber?: number | null;
}

interface GetChronologyProps {
  caseId: string;
  timelineChronologyKey?: TimelineChronologyTypeKeys;
  abortController?: AbortController;
}

const chronologyGetApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChronology: builder.query<ChronologyResponse, GetChronologyProps>({
      query: ({ caseId, abortController = new AbortController() }: GetChronologyProps) => ({
        url: `chronology?case_id=${caseId}`,
        method: 'GET',
        signal: abortController.signal
      }),
      providesTags: (_result, _err, { caseId }) => [{ type: CHRONOLOGY_TAG, id: caseId }],
      async onQueryStarted({ timelineChronologyKey }, { dispatch, queryFulfilled, getState }) {
        try {
          const response = await queryFulfilled;
          const state = getState() as RootState;
          const resolvedTimelineChronologyKey = timelineChronologyKey ?? state.sharedData.activeTimelineChronologyType;

          if (response.data && resolvedTimelineChronologyKey === TimelineChronologyType.CHRONOLOGY) {
            // Check if entryGroups exists and is an array
            if (!response.data.entryGroups || !Array.isArray(response.data.entryGroups)) {
              console.warn('chronologyGet: entryGroups is missing or not an array:', response.data);
              return;
            }
            let showDateBar = true;
            const data = {
              startDate: response.data.startDate,
              endDate: response.data.endDate,
              fullBundlePages: response.data.fullBundlePages,
              lightBundlePages: response.data.lightBundlePages,
              fullBundleId: response.data.fullBundleId,
              lightBundleId: response.data.lightBundleId,
              entries: response.data.entryGroups.flatMap((entryGroup: ChronologyEntryGroup) => {
                const groupDate = entryGroup.date;
                if (groupDate) showDateBar = false;
                const entries = entryGroup.entries || [];
                const mappedEntries = entries.map((entry: ChronologyEntry) => ({
                  id: entry.chronologyEntryId,
                  date: entry.date,
                  providerId: entry.providerId,
                  providerName: entry.providerName,
                  summary: entry.verbatimExtract,
                  comment: entry.comment,
                  pages: entry.pages,
                  time: entry.time,
                  containsAdditionalRecords: entry.containsAdditionalRecords
                }));
                return [groupDate, ...mappedEntries];
              })
            };
            dispatch(
              setTimelineChronologyData({
                key: resolvedTimelineChronologyKey,
                data
              })
            );
            dispatch(
              setShowDateBar({
                key: resolvedTimelineChronologyKey,
                data: showDateBar
              })
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getChronology in onQueryStarted:', error);
          }
        }
      }
    })
  })
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetChronologyQuery } = chronologyGetApi;
